import { useEffect } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { usePageContext } from '@hero/web-context';
import {
  BillingItemsCard,
  BillingPaymentHistory,
  BillingPaySummaryCard,
  PageBack,
} from '@hero/web-components';
import {
  useBillingApi,
  useBillingTransactionListApi,
} from '@hero/api-hooks';
import { useBillingPayManager } from '@hero/web-hooks';

/**
 * Receipt-style detail screen for a single Billing.
 *
 * Layout mirrors the checkout screen — left column for the items
 * breakdown + payment history (so the tall side flows together and
 * the right column doesn't leave a gap), sticky right column for the
 * "Amount due" summary + Pay-now CTA.
 *
 * The billing's identity (title + period + status pill) lives inside
 * `BillingItemsCard` itself — keeping it there means we don't render
 * the same title twice on the screen and the items card reads as the
 * primary "what is this billing" card without an extra strip above
 * it.
 */
export default function BillingDetailPage() {
  const { t } = useTranslation('m-billing');
  const { setPageName } = usePageContext();
  const { id } = useParams<{ id: string }>();
  const billingQuery = useBillingApi(id);
  const billing = billingQuery.data;
  const txListQuery = useBillingTransactionListApi({
    billingId: id,
  } as never);
  const transactions = txListQuery.data ?? [];
  const { payBilling } = useBillingPayManager();

  useEffect(() => {
    setPageName(t('billing_doc.title'));
  }, [setPageName, t]);

  if (!billing) {
    return (
      <div className="mt-6 flex flex-col gap-4">
        <div className="h-10 w-32 animate-pulse rounded-lg bg-gray-100" />
        <div className="grid gap-6 lg:grid-cols-12">
          <div className="h-72 w-full animate-pulse rounded-2xl bg-gray-100 lg:col-span-8" />
          <div className="h-72 w-full animate-pulse rounded-2xl bg-gray-100 lg:col-span-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 flex flex-col gap-4 pb-12">
      <PageBack redirect="billing" />

      {/* Items + payment history (left) + sticky pay summary (right).
          The title / period / status badge live inside
          `BillingItemsCard` itself, and payment history lives inside
          the left column so the tall side flows together — keeps the
          two-column rhythm even when the items list is short. */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
        <div className="flex flex-col gap-6 lg:col-span-8">
          <BillingItemsCard billing={billing} />
          <BillingPaymentHistory
            transactions={transactions}
            isLoading={txListQuery.isLoading}
            defaultOpen
          />
        </div>
        <div className="lg:col-span-4">
          <BillingPaySummaryCard
            billing={billing}
            onPay={() => payBilling(billing, { openIn: 'tab' })}
          />
        </div>
      </div>
    </div>
  );
}
